import React from 'react';
import { Form, Input, Select, Typography } from 'antd';
import { PolicyType } from '../../../types.generated';
import { useTranslation } from '../../../i18n/useTranslation';

type Props = {
    policyType: string;
    setPolicyType: (type: PolicyType) => void;
    policyName: string;
    setPolicyName: (name: string) => void;
    policyDescription: string;
    setPolicyDescription: (description: string) => void;
};

export default function PolicyTypeForm({
    policyType,
    setPolicyType,
    policyName,
    setPolicyName,
    policyDescription,
    setPolicyDescription,
}: Props) {
    const { t } = useTranslation();

    const updatePolicyName = (name: string) => {
        setPolicyName(name);
    };

    return (
        <Form layout="vertical">
            <Form.Item
                name="policyName"
                className="mb-4"
                label={
                    <Typography.Text>
                        <span>{t('Name')}</span>
                        <span className="f-color-risk-very-high pl-1">*</span>
                    </Typography.Text>
                }
            >
                <Input
                    placeholder={t('Your policy name')}
                    data-testid="policy-name"
                    value={policyName}
                    onChange={(event) => updatePolicyName(event.target.value)}
                />
                <Typography.Paragraph className="f-text-content mt-1 mb-0">
                    {t('A name for your new policy.')}
                </Typography.Paragraph>
            </Form.Item>
            <Form.Item
                name="policyType"
                className="mb-4"
                label={
                    <Typography.Text>
                        <span>{t('Type')}</span>
                        <span className="f-color-risk-very-high pl-1">*</span>
                    </Typography.Text>
                }
            >
                <Typography.Paragraph>{t('The type of policy you would like to create.')}</Typography.Paragraph>
                <Select
                    data-testid="policy-type"
                    aria-label={t('Policy Type')}
                    defaultValue={policyType}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    onSelect={(value) => setPolicyType(value as PolicyType)}
                >
                    <Select.Option data-testid="platform" value={PolicyType.Platform} tabIndex={0}>
                        {t('Platform')}
                    </Select.Option>
                    <Select.Option data-testid="metadata" value={PolicyType.Metadata} tabIndex={0}>
                        {t('Metadata')}
                    </Select.Option>
                </Select>
                <Typography.Paragraph className="f-text-content mt-1 mb-0">
                    {t('The type of policy you would like to create.')}
                </Typography.Paragraph>
                <Typography.Paragraph type="secondary" className="mt-3 mb-0">
                    {t('CREATE_POLICY_DESCRIPTION')}
                </Typography.Paragraph>
                <Typography.Paragraph type="secondary" className="mt-3 mb-0">
                    {t('CREATE_POLICY_DESCRIPTION_METADATA')}
                </Typography.Paragraph>
            </Form.Item>
            <Form.Item name="policyDescription" label={t('Description')} className="mb-4">
                <Input
                    placeholder={t('Your policy description')}
                    data-testid="policy-description"
                    value={policyDescription}
                    onChange={(event) => setPolicyDescription(event.target.value)}
                />
                <Typography.Paragraph className="f-text-content mt-1 mb-0">
                    {t('An optional description for your new policy.')}
                </Typography.Paragraph>
            </Form.Item>
        </Form>
    );
}
